import React from 'react';
import { MessageSquare, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { api } from '../../services/api';

export const OffersTab = ({ offers, fetchData, setCounterFor, setCounterForm, formatCurrency }) => {
  if (offers.length === 0) {
    return (
      <div data-testid="offers-tab">
        <div className="luxury-card p-12 text-center">
          <MessageSquare size={48} className="text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-white font-serif text-xl mb-2">No Offers Yet</h3>
          <p className="text-muted-foreground text-sm">Make a custom offer on any listing or wait for buyers to negotiate on your tokens.</p>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="offers-tab">
      <div className="space-y-3">
        {offers.map(o => {
          const isMyOffer = o.my_role === 'buyer';
          return (
            <div key={o.id} className="luxury-card p-5" data-testid={`offer-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${isMyOffer ? 'bg-[#627EEA]/15 text-[#627EEA]' : 'bg-gold/15 text-gold'}`}>
                      {isMyOffer ? 'You offered' : 'Received'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] capitalize ${
                      o.status === 'pending' ? 'bg-amber-500/15 text-amber-400' :
                      o.status === 'accepted' ? 'bg-emerald-500/15 text-emerald-400' :
                      o.status === 'countered' ? 'bg-purple-500/15 text-purple-400' :
                      'bg-muted/15 text-muted-foreground'
                    }`}>{o.status}{o.last_counter_by ? ` (by ${o.last_counter_by})` : ''}</span>
                  </div>
                  <p className="text-white text-sm font-medium mt-1">{o.asset_name}</p>
                  <p className="text-xs text-muted-foreground">{o.fractions} fractions @ {formatCurrency(o.price_per_fraction)} = {formatCurrency(o.total_value)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">List price was {formatCurrency(o.list_price_at_offer)}</p>
                  {o.message && <p className="text-xs text-muted-foreground italic mt-2">"{o.message}"</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {isMyOffer && o.status === 'accepted' && (
                    <Button size="sm" className="bg-[#627EEA] hover:bg-[#5570D0] text-white text-xs" data-testid={`pay-offer-${o.id}`} onClick={async () => {
                      try {
                        const res = await api.createOfferCheckout(o.id);
                        if (res.data?.checkout_url) {
                          toast.success('Redirecting to secure payment...');
                          setTimeout(() => { window.location.href = res.data.checkout_url; }, 400);
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Unable to start payment.');
                      }
                    }}>
                      <ShoppingCart size={12} className="mr-1" /> Pay Now {formatCurrency(o.total_value)}
                    </Button>
                  )}
                  {isMyOffer && (o.status === 'pending' || o.status === 'countered') && (
                    <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" data-testid={`withdraw-offer-${o.id}`} onClick={async () => {
                      try { await api.withdrawOffer(o.id); toast.success('Offer withdrawn.'); fetchData(); }
                      catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }}>Withdraw</Button>
                  )}
                  {!isMyOffer && o.status === 'pending' && (
                    <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`accept-offer-${o.id}`} onClick={async () => {
                        try { await api.respondToOffer(o.id, { action: 'accept' }); toast.success('Offer accepted.'); fetchData(); }
                        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}>Accept</Button>
                      <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 text-xs" data-testid={`counter-offer-${o.id}`} onClick={() => {
                        setCounterFor(o);
                        setCounterForm({ price: String(o.price_per_fraction), fractions: String(o.fractions), message: '' });
                      }}>Counter</Button>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" data-testid={`reject-offer-${o.id}`} onClick={async () => {
                        try { await api.respondToOffer(o.id, { action: 'reject' }); toast.success('Offer rejected.'); fetchData(); }
                        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
              {o.history && o.history.length > 1 && (
                <details className="mt-2">
                  <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-white">{o.history.length} message(s) in this negotiation</summary>
                  <div className="mt-2 space-y-1 pl-3 border-l border-[#1A1A1A]">
                    {o.history.map((h, i) => (
                      <div key={i} className="text-[11px] text-muted-foreground">
                        <span className="text-gold uppercase">{h.by}</span> {h.action}
                        {h.price_per_fraction ? ` @ ${formatCurrency(h.price_per_fraction)} × ${h.fractions}` : ''}
                        {h.message ? ` — "${h.message}"` : ''}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
